import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { cn } from '@repo/ui/src/lib/utils';
import { useSessions } from '../hooks/useSessions';
import { useEffectiveBranchId } from '../hooks/useBranchScope';
import { monthRange, shortWeekday } from '../lib/sessionTime';
import { pivotAttendanceGrid, type AttendanceStatus } from '../lib/attendanceGrid';
import { normalizeForSearch } from '../lib/textSearch';
import {
  filterGridRows,
  filterSessionsForGrid,
  type AttendanceMonthFilter,
} from '../lib/attendanceMonthFilter';

const dmf = new Intl.DateTimeFormat('vi-VN', { timeZone: 'UTC', day: '2-digit', month: '2-digit' });
const fmtDate = (d: string) => dmf.format(new Date(`${d}T00:00:00Z`));

const GLYPH: Record<AttendanceStatus, string> = { PRESENT: '✓', EXCUSED: 'P', ABSENT: '✗', PENDING: '?' };
const GLYPH_CLASS: Record<AttendanceStatus, string> = {
  PRESENT: 'text-brand-600',
  EXCUSED: 'text-muted-foreground',
  ABSENT: 'text-red-600',
  // The linkable "?" cell (below) adds its own tint, size and ring — this is
  // only its glyph colour. The un-linkable fallback (no sessionId) restores
  // the old `bg-amber-100 rounded font-semibold` inline, since it isn't a
  // `Link` and gets none of that styling from the shared `cls`.
  PENDING: 'text-attention-foreground',
};

// Fixed px, never `--spacing` multiples: the app sets `--spacing: 0.2rem`
// globally, far too coarse to size grid structure with. DAY_W is measured,
// not guessed — at this table's real 14px `text-sm` (not 12px) with `px-2`
// padding, 44px clipped "08/08" by 9px in both the webfont and its system-ui
// fallback; 56px was the smallest that cleared it with margin.
const NAME_W = 148;
const DAY_W = 56;
const LAST_W = 56;
/** The count columns at the end of a row: Phép, Bù, Vắng, Chưa. */
const COUNT_COLUMNS = 4;

/** One column grid shared by the header and body tables below — table-layout:
 *  fixed only reads `<col>` widths (or the table's own explicit `width`),
 *  never cell content, so this is what keeps the two tables' columns
 *  pixel-identical. */
function Colgroup({ dayCount }: { dayCount: number }) {
  return (
    <colgroup>
      <col style={{ width: 'var(--name-w)' }} />
      {Array.from({ length: dayCount }, (_, i) => (
        <col key={i} style={{ width: 'var(--day-w)' }} />
      ))}
      {Array.from({ length: COUNT_COLUMNS }, (_, i) => (
        <col key={i} style={{ width: 'var(--last-w)' }} />
      ))}
    </colgroup>
  );
}

/** A count at the end of a row: zero recedes, as the Chưa column's does. */
function CountCell({ value, className }: { value: number; className?: string }) {
  return (
    <td className="text-center px-2 py-2 border-t">
      <span className={cn('tabular', value > 0 ? className : 'text-muted-foreground')}>{value}</span>
    </td>
  );
}

export interface AttendanceGridProps {
  /** 'YYYY-MM'. The grid owns the fetch for this month — `GET /sessions?from&to`,
   *  which is also what materialises a current/future month's sessions server-side. */
  month: string;
  /** Heading above the grid. */
  title: string;
  /** One muted line under the heading. Omitted = no line. */
  hint?: string;
  /**
   * The built-in search box and "Xem tất cả / Chỉ xem còn thiếu" toggle.
   *
   * Chốt sổ keeps them (default `true`) because there the grid is one block on
   * a busy page and owns its own controls. `/attendance` passes `false`: it
   * owns richer controls in its page chrome, and with the toggle gone the grid
   * shows the WHOLE class — exceptions-only is a close-month idea, not a
   * roster idea.
   */
  builtInControls?: boolean;
  /** Narrowing owned by the caller, applied around the pivot. See `lib/attendanceMonthFilter`. */
  filter?: AttendanceMonthFilter;
  /** Replaces the plain sentence shown when the month holds no sessions at all. */
  emptyMonth?: ReactNode;
  /** Replaces the plain sentence shown when sessions exist but no row survives. */
  emptyFiltered?: ReactNode;
  /** Shown while the month loads. Default `null` — Chốt sổ renders nothing. */
  loadingFallback?: ReactNode;
}

export function AttendanceGrid({
  month,
  title,
  hint,
  builtInControls = true,
  filter,
  emptyMonth,
  emptyFiltered,
  loadingFallback = null,
}: AttendanceGridProps) {
  const { t } = useTranslation('scheduling');
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const range = useMemo(() => monthRange(month), [month]);
  const branchId = useEffectiveBranchId();
  const { data: sessions, isLoading } = useSessions({ ...range, branchId });

  // Subject/mode narrow BEFORE the pivot so a filtered-out session stops
  // producing a date column at all; search/student narrow rows AFTER it.
  const filtered = useMemo(() => filterSessionsForGrid(sessions ?? [], filter), [sessions, filter]);
  const grid = useMemo(() => pivotAttendanceGrid(filtered), [filtered]);

  // Whether the MONTH (pre-filter) has any real session — decides which empty
  // story to tell. `grid.dates.length === 0` alone conflates "no sessions this
  // month" with "a subject/mode filter narrowed the pivot to nothing", which
  // left a filtered-empty month showing the quiet "no sessions" copy with no
  // escape back to the filter. Chốt sổ passes no filter, so `filtered` and
  // `sessions` agree there and this is unchanged for it.
  // Participants, not sessions: `GET /sessions?from&to` top-ups ACTIVE slots
  // for whatever range it is asked, so a month from before anyone joined
  // still comes back with (participant-less) sessions — the workbench day
  // navigator has the same behaviour. "The month is empty" must therefore
  // mean "nobody attended anything", or an untouched past month would fall
  // into the no-row branch and read as a filter miss with no filter active.
  const monthHasSessions = (sessions ?? []).some(
    (s) => s.status !== 'CANCELLED' && s.participants.length > 0,
  );

  // With no built-in toggle there is nothing to fold rows away with, and the
  // whole class is the point of the standalone screen.
  const showingAll = builtInControls ? showAll : true;
  // Same normalization `filterGridRows` applies to `q` — a query that is
  // non-empty but normalizes away to nothing (e.g. bare diacritics) must read
  // as "no filter" here too, matching what the row filter actually did.
  const normalizedSearch = normalizeForSearch(search.trim());
  const rows = filterGridRows(
    grid.students.filter((s) => showingAll || (grid.pendingByStudent[s.studentId] ?? 0) > 0),
    // The built-in box and the caller's `q` never both exist: Chốt sổ passes no
    // filter, `/attendance` passes no built-in controls.
    builtInControls ? { ...filter, q: search } : filter,
    // Safe to sort in place: the array above is always freshly built by `.filter`.
  ).sort((a, b) => a.studentName.localeCompare(b.studentName, 'vi'));

  const countLabels = [t('attendanceGrid.excused'), t('attendanceGrid.madeUp'), t('attendanceGrid.absent'), t('attendanceGrid.pending')];

  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Whether the split table further down is actually in the DOM right now —
  // loading, an empty state, and a search that zeroes `rows` all swap it out,
  // destroying and later recreating both ref'd nodes.
  const gridVisible = monthHasSessions && rows.length > 0;

  // The header table sits in no scroll container of its own (see the comment
  // below), so it never receives scroll events and has to be told to follow
  // the body table's. Keyed on `gridVisible` rather than `[]` or every
  // render: a fresh mount needs the listener re-attached and the header
  // re-synced, but a re-render that leaves the table mounted (typing in the
  // search box) does not.
  useEffect(() => {
    const headerEl = headerRef.current;
    const bodyEl = bodyRef.current;
    if (!headerEl || !bodyEl) return;
    const sync = () => { headerEl.scrollLeft = bodyEl.scrollLeft; };
    sync(); // in case the two ever start out of step on a fresh mount
    bodyEl.addEventListener('scroll', sync, { passive: true });
    return () => bodyEl.removeEventListener('scroll', sync);
  }, [gridVisible]);

  if (isLoading) return <>{loadingFallback}</>;

  return (
    <div id="attendance-grid" className="border rounded-md bg-card p-4 mb-6 scroll-mt-6">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-display text-[15px] font-semibold text-surface-900">{title}</h2>
          {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
        </div>
        {builtInControls && (
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('attendanceGrid.searchPlaceholder')}
              className="h-8 w-48"
            />
            <Button
              type="button"
              size="sm"
              variant={showAll ? 'default' : 'outline'}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? t('attendanceGrid.showExceptions') : t('attendanceGrid.showAll')}
            </Button>
          </div>
        )}
      </div>

      {!monthHasSessions ? (
        emptyMonth ?? <p className="text-sm text-muted-foreground">{t('attendanceGrid.empty')}</p>
      ) : rows.length === 0 ? (
        emptyFiltered ?? (
          <p className="text-sm text-muted-foreground">
            {showingAll || normalizedSearch ? t('attendanceGrid.noMatch') : t('attendanceGrid.allClear', { month })}
          </p>
        )
      ) : (
        // Header and body are two <table>s sharing one column grid, not one
        // scrolling wrapper: overflow-x:auto on a shared wrapper is the
        // sticky scrollport for BOTH axes, so `sticky top-0` pins to the
        // wrapper's own top and scrolls away with it — dead in a harness,
        // with or without overflow-y:clip. The header div below sits in no
        // scroll container of its own, so its `sticky top-0` resolves against
        // the page scroller instead and actually holds at the viewport edge;
        // the body div owns the horizontal scrollbar, and the effect above
        // mirrors its scrollLeft onto the header. Both tables carry an
        // explicit `width` because table-layout:fixed is ignored without
        // one, and the frozen column carries its own explicit width because
        // position:sticky on a cell defeats that enforcement for just that
        // column — unpinned, those two were a 35.8px/136.6px header/body
        // mismatch.
        <div
          className="relative"
          style={{
            '--name-w': `${NAME_W}px`,
            '--day-w': `${DAY_W}px`,
            '--last-w': `${LAST_W}px`,
            '--grid-w': `${NAME_W + grid.dates.length * DAY_W + COUNT_COLUMNS * LAST_W}px`,
          } as CSSProperties}
        >
          <div ref={headerRef} className="sticky top-0 z-30 overflow-hidden bg-card border-b">
            <table aria-hidden="true" style={{ width: 'var(--grid-w)' }} className="table-fixed border-separate border-spacing-0 text-sm">
              <Colgroup dayCount={grid.dates.length} />
              <thead>
                <tr>
                  <th
                    style={{ width: 'var(--name-w)', minWidth: 'var(--name-w)', maxWidth: 'var(--name-w)' }}
                    className="text-left font-medium text-muted-foreground pr-3 pb-2 sticky left-0 z-20 bg-card"
                  >
                    {t('attendanceGrid.student')}
                  </th>
                  {grid.dates.map((d) => (
                    <th key={d} className="text-center font-medium text-muted-foreground px-2 pb-2 whitespace-nowrap">
                      <div className="flex flex-col items-center leading-tight">
                        <span>{fmtDate(d)}</span>
                        <span className="text-[10px] font-normal uppercase">{shortWeekday(d)}</span>
                      </div>
                    </th>
                  ))}
                  {countLabels.map((label) => (
                    <th key={label} className="text-center font-medium text-muted-foreground px-2 pb-2 whitespace-nowrap">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
          <div ref={bodyRef} className="overflow-x-auto">
            <table style={{ width: 'var(--grid-w)' }} className="table-fixed border-separate border-spacing-0 text-sm">
              <Colgroup dayCount={grid.dates.length} />
              {/* Real headers so body <td>s keep their <th> association for
                  screen readers now that the visible copy above is
                  aria-hidden. Measured before shipping: `sr-only` (clip-based,
                  keeps `display: table-cell`/`table-row` on the cells) matches
                  the visible header's column widths exactly and still exposes
                  its text; a zero-height/zero-padding/text-[0px] alternative
                  measured worse — it dropped the header text from the page's
                  accessible content entirely. */}
              <thead className="sr-only">
                <tr>
                  <th>{t('attendanceGrid.student')}</th>
                  {grid.dates.map((d) => (
                    <th key={d}>{fmtDate(d)} {shortWeekday(d)}</th>
                  ))}
                  {countLabels.map((label) => (
                    <th key={label}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const pending = grid.pendingByStudent[s.studentId] ?? 0;
                  const fullDisplayName = s.studentNickname ? `${s.studentName} (${s.studentNickname})` : s.studentName;
                  return (
                    <tr key={s.studentId}>
                      <td
                        style={{ width: 'var(--name-w)', minWidth: 'var(--name-w)', maxWidth: 'var(--name-w)' }}
                        className="py-2 pr-3 whitespace-nowrap sticky left-0 z-[1] bg-card border-t"
                      >
                        <Link
                          to={`/students/${s.studentId}`}
                          title={fullDisplayName}
                          className="hover:underline text-brand-700 font-medium block max-w-[var(--name-w)] truncate sm:max-w-none"
                        >
                          {fullDisplayName}
                        </Link>
                      </td>
                      {grid.dates.map((d) => {
                        const status = grid.cell(s.studentId, d);
                        const sessionId = grid.sessionIdAt(s.studentId, d);
                        const glyph = status ? GLYPH[status] : '—';
                        const cls = status ? GLYPH_CLASS[status] : 'text-muted-foreground';
                        return (
                          <td key={d} className="text-center px-2 py-2 border-t">
                            {status && sessionId ? (
                              <Link
                                to={`/sessions/${sessionId}`}
                                // Chốt sổ moves focus to the first of these.
                                data-pending-cell={status === 'PENDING' ? '' : undefined}
                                aria-label={t(status === 'PENDING' ? 'attendanceGrid.markCellLabel' : 'attendanceGrid.openCellLabel', { date: fmtDate(d) })}
                                title={status === 'PENDING' ? t('attendanceGrid.markCta') : t('attendanceGrid.openCta')}
                                className={cn(
                                  'inline-flex size-[22.4px] items-center justify-center rounded outline-none transition focus-visible:ring-2',
                                  // Only the unmarked cell is tinted: it is the
                                  // one that asks for something. A marked cell
                                  // keeps its bare glyph.
                                  status === 'PENDING'
                                    ? 'bg-attention-tint font-semibold hover:ring-2 hover:ring-attention focus-visible:ring-attention'
                                    : 'focus-visible:ring-brand-500',
                                  cls,
                                )}
                              >
                                {glyph}
                              </Link>
                            ) : (
                              <span className={cn(cls, status === 'PENDING' && 'bg-amber-100 rounded font-semibold')}>{glyph}</span>
                            )}
                          </td>
                        );
                      })}
                      <CountCell value={grid.excusedByStudent[s.studentId] ?? 0} className="text-surface-900" />
                      <CountCell value={grid.madeUpByStudent[s.studentId] ?? 0} className="text-surface-900" />
                      <CountCell value={grid.absentByStudent[s.studentId] ?? 0} className="font-medium text-red-600" />
                      <td className="text-center px-2 py-2 border-t">
                        {pending > 0 ? (
                          <Badge variant="warning" role="img" aria-label={t('attendanceGrid.pendingCountLabel', { count: pending })} className="tabular px-2 py-0.5">
                            {pending}
                          </Badge>
                        ) : (
                          <span className="tabular text-muted-foreground">0</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
